import DeleteDialog from '@/components/common/delete-dialog/delete-dialog';
import GetMcContainer from '@/components/common/get-mc-container/get-mc-container';
import ShowQrDialog from '@/components/common/show-qr-dialog/show-qr-dialog';
import AccordionItem from '@/components/navigation/navigation-items/accordion-item';
import AccordionItemSkeleton from '@/components/navigation/navigation-items/accordion-item-skeleton';
import PlacesList from '@/components/navigation/navigation-items/places-list';
import CreateSiteDialog from '@/components/site/create-site-dialog';
import EditSiteDialog from '@/components/site/edit-site-dialog';
import { composeUri } from '@/utils/compose';
import {
    AVATARS,
    EDIT_SITE_PARENT_ROUTE,
    ROLE_BIG_CHANGE_PARENT_SITE,
    ROLE_BIG_CREATE_SITE,
    ROLE_BIG_DELETE_SITE,
    ROLE_BIG_LINK_USER_TO_SITE,
    ROLE_BIG_READ_SITE,
    ROLE_BIG_UPDATE_SITE,
    SITE_USERS_LIST_ROUTE,
    SITES,
    SITES_METADATA_AVATAR_TYPE_NAME,
} from '@/utils/CONST';
import extractIdFromURI from '@/utils/id';
import { Avatar } from '@/utils/types';
import { DeleteOutlined, EditOutlined, PersonOutlined } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import ChangeCircleOutlinedIcon from '@mui/icons-material/ChangeCircleOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { map, nth, range } from 'lodash';
import { FilterContext, InfinitePagination } from 'ra-ui-materialui';
import { forwardRef, Fragment, useImperativeHandle, useRef, useState } from 'react';
import {
    FilterForm,
    InfiniteListBase,
    TextInput,
    useDelete,
    useNotify,
    usePermissions,
    useTranslate,
    useUnselectAll,
    WithListContext,
} from 'react-admin';
import { useNavigate, useParams } from 'react-router';

interface Props {
    clientUriId?: string;
}

interface SitesListHandle {
    openCreateDialog: (clientId: string) => void;
}

const SitesList = forwardRef<SitesListHandle, Props>(({ clientUriId }, ref) => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const [deleteOne, { isLoading: isLoadingDelete }] = useDelete();
    const notify = useNotify();
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState<string>('');
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [selectedSiteId, setSelectedSiteId] = useState<string>('');
    const [selectedClientId, setSelectedClientId] = useState<string>('');
    const unselectAll = useUnselectAll(AVATARS);
    const { permissions } = usePermissions();
    const [showQrDialogState, setShowQrDialogState] = useState<boolean>(false);
    const [qrDialogInfo, setQrDialogInfo] = useState<any>(undefined);

    const { siteId, placeId } = useParams();
    const placesListRefs = useRef<Map<string, { openCreateDialog: (siteId: string) => void } | null>>(new Map());

    useImperativeHandle(ref, () => ({
        openCreateDialog: (clientId: string) => {
            setSelectedClientId(clientId);
            setOpenCreateDialog(true);
        },
    }));

    const navigateToEdit = (siteId: string, siteClientId?: string) => {
        setSelectedSiteId(siteId);
        setSelectedClientId(siteClientId ?? extractIdFromURI(clientUriId ?? '') ?? '');
        setOpenEditDialog(true);
    };

    const navigateToUsersList = (siteId: string) => {
        navigate(`${SITE_USERS_LIST_ROUTE}/${siteId}`);
    };

    const navigateToChangeParent = (siteId: string) => {
        if (!clientUriId) return;
        unselectAll();
        navigate(`${EDIT_SITE_PARENT_ROUTE}/${extractIdFromURI(clientUriId)}/${siteId}`);
    };

    const navigateToCreatePlace = (siteId: string) => {
        const siteUriId = composeUri(AVATARS, siteId);
        const placesListRef = placesListRefs.current.get(siteUriId);
        placesListRef?.openCreateDialog(siteId);
    };

    const handleSelect = (id: string, siteParentUriId?: string) => {
        const clientIdParam = clientUriId ? extractIdFromURI(clientUriId) : extractIdFromURI(siteParentUriId ?? '');
        navigate(`client/${clientIdParam}/site/${id}`);
    };

    const handleShowQrDialogClose = () => {
        setShowQrDialogState(false);
        setQrDialogInfo(undefined);
    };

    const handleRemoveAvatar = (avatarUriId: string) => {
        if (!!deleteId)
            deleteOne(
                clientUriId ? `${SITES}_${extractIdFromURI(clientUriId)}` : SITES,
                {
                    id: avatarUriId,
                    meta: {
                        requestType: 'update',
                        id: `${avatarUriId}/enable`,
                        data: {
                            enabled: false,
                            children: true,
                        },
                        realResource: AVATARS,
                    },
                },
                {
                    mutationMode: 'optimistic',
                    onSuccess: () => {
                        notify('ra.notification.deleted', {
                            type: 'success',
                            messageArgs: { smart_count: 1 },
                        });
                        setOpenDeleteModal(false);
                        setDeleteId('');
                    },
                },
            );
    };

    return (
        <Box>
            <InfiniteListBase
                resource={clientUriId ? `${SITES}_${extractIdFromURI(clientUriId)}` : SITES}
                perPage={30}
                sort={{
                    field: 'creationDate',
                    order: 'ASC',
                }}
                filter={{
                    'metadataAvatarType.name': SITES_METADATA_AVATAR_TYPE_NAME,
                    ...(clientUriId && {
                        parent: clientUriId,
                    }),
                    enabled: true,
                }}
                storeKey={clientUriId ? `siteList-${extractIdFromURI(clientUriId)}` : 'siteList-standalone'}
                disableSyncWithLocation
                queryOptions={{
                    enabled: permissions?.includes(ROLE_BIG_READ_SITE),
                    meta: {
                        realResource: AVATARS,
                    },
                }}
            >
                <WithListContext
                    render={({ data, isLoading, total }) => (
                        <Fragment>
                            {!clientUriId && (
                                <Box
                                    sx={{
                                        '& .RaFilterFormInput-spacer': {
                                            display: 'none',
                                        },
                                        '& form': {
                                            minHeight: 0,
                                        },
                                        '& form div': {
                                            width: '100%',
                                        },
                                        '& form .RaFilterForm-clearFix': {
                                            display: 'none',
                                        },
                                    }}
                                >
                                    <FilterContext.Provider
                                        value={[
                                            <TextInput
                                                name='name'
                                                placeholder={translate('main.searchSite')}
                                                label={false}
                                                source='name'
                                                helperText={false}
                                                sx={{
                                                    '& .MuiInputBase-root': {
                                                        borderRadius: 0,
                                                    },
                                                    mt: '0 !important',
                                                }}
                                                alwaysOn
                                            />,
                                        ]}
                                    >
                                        <FilterForm />
                                    </FilterContext.Provider>
                                </Box>
                            )}

                            {data && data.length === 0 && (
                                <Typography
                                    sx={{
                                        fontSize: 14,
                                        textAlign: 'center',
                                        color: (theme) => theme.palette.action.disabled,
                                        py: 1,
                                    }}
                                >
                                    {translate('site.no_items')}
                                </Typography>
                            )}

                            {map(data, ({ id, name, mcs, parent }: Avatar) => (
                                <GetMcContainer mcUriId={nth(mcs, 0)} key={id}>
                                    {() => (
                                        <AccordionItem
                                            id={id}
                                            key={id}
                                            name={name}
                                            {...(clientUriId && {
                                                pl: 3,
                                            })}
                                            onSelect={() => handleSelect(extractIdFromURI(id) ?? '', parent?.id)}
                                            selected={extractIdFromURI(id) === siteId && !placeId}
                                            defaultExpanded={extractIdFromURI(id) === siteId}
                                            menuItems={[
                                                {
                                                    label: translate('ra.action.edit'),
                                                    icon: <EditOutlined />,
                                                    onClick: () =>
                                                        navigateToEdit(
                                                            extractIdFromURI(id) as string,
                                                            extractIdFromURI(parent?.id ?? '') ?? '',
                                                        ),
                                                    disabled: !permissions?.includes(ROLE_BIG_UPDATE_SITE),
                                                },
                                                {
                                                    label: translate('ra.action.delete'),
                                                    icon: <DeleteOutlined />,
                                                    onClick: () => {
                                                        setOpenDeleteModal(true);
                                                        setDeleteId(id);
                                                    },
                                                    disabled: !permissions?.includes(ROLE_BIG_DELETE_SITE),
                                                },
                                                {
                                                    label: translate('main.navigation.users'),
                                                    icon: <PersonOutlined />,
                                                    onClick: () =>
                                                        navigateToUsersList(extractIdFromURI(id) as string),
                                                    test: 'user-list',
                                                    disabled: !permissions?.includes(ROLE_BIG_LINK_USER_TO_SITE),
                                                },
                                                {
                                                    label: translate('main.navigation.change_client'),
                                                    icon: <ChangeCircleOutlinedIcon />,
                                                    onClick: () =>
                                                        navigateToChangeParent(
                                                            extractIdFromURI(id) as string,
                                                        ),
                                                    disabled: !permissions?.includes(ROLE_BIG_CHANGE_PARENT_SITE),
                                                },
                                                {
                                                    label: translate('buttons.add_place'),
                                                    icon: <AddIcon />,
                                                    onClick: () =>
                                                        navigateToCreatePlace(extractIdFromURI(id) as string),
                                                    disabled: !permissions?.includes(ROLE_BIG_CREATE_SITE),
                                                },
                                            ]}
                                        >
                                            <PlacesList
                                                siteUriId={id}
                                                clientUriId={clientUriId ?? parent?.id}
                                                ref={(node) => {
                                                    if (node) {
                                                        placesListRefs.current.set(id, node);
                                                    } else {
                                                        placesListRefs.current.delete(id);
                                                    }
                                                }}
                                            />
                                        </AccordionItem>
                                    )}
                                </GetMcContainer>
                            ))}

                            {isLoading && range(3).map((index) => <AccordionItemSkeleton key={index} />)}

                            {data && total !== undefined && data.length < total && <InfinitePagination />}

                            {openDeleteModal && (
                                <DeleteDialog
                                    isLoading={isLoadingDelete}
                                    onClose={() => setOpenDeleteModal(false)}
                                    onClick={() => handleRemoveAvatar(deleteId)}
                                />
                            )}
                        </Fragment>
                    )}
                />
            </InfiniteListBase>

            {qrDialogInfo !== undefined && showQrDialogState && (
                <ShowQrDialog
                    open={showQrDialogState}
                    onClose={handleShowQrDialogClose}
                    url={qrDialogInfo.url}
                    downloadName={qrDialogInfo.name}
                />
            )}

            {openCreateDialog && selectedClientId && (
                <CreateSiteDialog
                    open={openCreateDialog}
                    onClose={() => {
                        setOpenCreateDialog(false);
                        setSelectedClientId('');
                    }}
                    clientId={selectedClientId}
                />
            )}

            {openEditDialog && selectedSiteId && (
                <EditSiteDialog
                    open={openEditDialog}
                    onClose={() => {
                        setOpenEditDialog(false);
                        setSelectedSiteId('');
                    }}
                    siteId={selectedSiteId}
                    clientId={selectedClientId}
                />
            )}
        </Box>
    );
});

SitesList.displayName = 'SitesList';

export default SitesList;
